/**
 * Form Validation Utilities
 *
 * Pure validators that return an i18n key when a value is invalid,
 * or `null` when the value passes. Callers translate the returned key
 * via react-admin's `useTranslate`.
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EMAIL_MIN_LENGTH = 3;
export const EMAIL_MAX_LENGTH = 320;
export const PASSWORD_MIN_LENGTH = 3;
export const PASSWORD_MAX_LENGTH = 100;

export const validateEmail = (value: string): string | null => {
  const v = value.trim();
  if (!v) return 'validation.required';
  if (v.length < EMAIL_MIN_LENGTH) return 'validation.minLength';
  if (v.length > EMAIL_MAX_LENGTH) return 'validation.maxLength';
  if (!EMAIL_PATTERN.test(v)) return 'validation.email';
  return null;
};

export const validatePassword = (value: string): string | null => {
  if (!value) return 'validation.required';
  if (value.length < PASSWORD_MIN_LENGTH) return 'validation.minLength';
  if (value.length > PASSWORD_MAX_LENGTH) return 'validation.maxLength';
  return null;
};

// ---- Numeric form inputs -------------------------------------------------

/**
 * `<TextInput type="number">` emits strings. This parser normalizes to
 * `number | null` so form state stays typed as `number` end-to-end.
 * Pair it with the RA input's `parse` prop.
 */
export const parseNumericInput = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

/** Validator: must be 0 or greater. Returns an RA translation key when invalid. */
export const nonNegativeNumber = (value: unknown): string | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const n = Number(value);
  if (Number.isNaN(n)) return 'ra.validation.number';
  if (n < 0) return 'validation.nonNegative';
  return undefined;
};

/** Validator: must be strictly greater than 0. Returns an RA translation key when invalid. */
export const positiveNumber = (value: unknown): string | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const n = Number(value);
  if (Number.isNaN(n)) return 'ra.validation.number';
  if (n <= 0) return 'validation.positive';
  return undefined;
};
